import React, { Suspense, useMemo, useState, useRef, useEffect } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Stars } from "@react-three/drei";
import Grass, { getYPosition } from "./Grass";
import SpotifyPlayer from "./SpotifyPlayer";
import FlyingBugs from "./FlyingBugs";
import Rain from "./Rain";
import ReflexionBox from "./ReflexionBox";
import Mushroom from "./Mushroom";
import LoadingScreen from "./LoadingScreen";
import { PERF } from "./performanceConfig";

export default function App() {
  // Terrain bounds — must match width prop passed to <Grass /> below.
  const terrainWidth = 300;
  const terrainHalf = terrainWidth / 2;
  // Generar trayectorias únicas y persistentes para todos los mosquitos
  const bugTrajectories = useMemo(
    () =>
      [...Array(PERF.bugCount)].map((_, i) => {
        // Para algunos, forzar que lleguen a los bordes
        let baseX, ampX, baseZ, ampZ;
        if (i < 10) {
          // Mosquitos que van de borde a borde en X
          baseX = 0;
          ampX = terrainHalf;
        } else {
          baseX = Math.random() * 16 - 8;
          ampX = 30 + Math.random() * 5;
        }
        if (i >= 10 && i < 20) {
          // Mosquitos que van de borde a borde en Z
          baseZ = 0;
          ampZ = terrainHalf;
        } else {
          baseZ = Math.random() * 16 - 8;
          ampZ = 30 + Math.random() * 5;
        }
        return {
          baseX,
          baseY: 8 + Math.random() * 6,
          baseZ,
          ampX,
          ampY: 2 + Math.random() * 2,
          ampZ,
          speedX: 0.08 + Math.random() * 0.13, // más lento
          speedY: 0.12 + Math.random() * 0.18, // más lento
          speedZ: 0.08 + Math.random() * 0.13, // más lento
          phaseX: Math.random() * Math.PI * 2,
          phaseY: Math.random() * Math.PI * 2,
          phaseZ: Math.random() * Math.PI * 2,
        };
      }),
    [],
  );

  // Generar posiciones aleatorias para los hongos 3D
  const generateMushroom = () => {
    let x, z, y;
    let attempts = 0;
    do {
      x = Math.random() * terrainWidth - terrainWidth / 2;
      z = Math.random() * terrainWidth - terrainWidth / 2;
      y = getYPosition(x, z) + 2.5;
      attempts++;
      // Evita zonas muy bajas (enterrados) y fuera del rango visible
    } while ((y < 1 || y > 10) && attempts < 50);
    const scale = 0.9;
    const rotY = Math.random() * Math.PI * 2;
    return { position: [x, y, z], scale, rotation: [0, rotY, 0] };
  };
  const [mushrooms] = useState(() => [
    generateMushroom(),
    generateMushroom(),
    generateMushroom(),
    generateMushroom(),
    generateMushroom(),
  ]);

  const [rainActive, setRainActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [nightMode, setNightMode] = useState(false);
  const [grassLoaded, setGrassLoaded] = useState(false);
  const hideUITimer = useRef();
  const audioRef = useRef(null);

  useEffect(() => {
    document.body.classList.toggle("night-mode", nightMode);
  }, [nightMode]);

  useEffect(() => {
    const handleMouseMove = () => {
      document.body.classList.remove("ui-hidden");
      if (hideUITimer.current) clearTimeout(hideUITimer.current);
      hideUITimer.current = setTimeout(() => {
        document.body.classList.add("ui-hidden");
      }, 4000);
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (hideUITimer.current) clearTimeout(hideUITimer.current);
    };
  }, []);

  const handleToggleRain = () => {
    if (!rainActive) {
      setRainActive(true);
      if (audioRef.current) {
        audioRef.current.muted = false;
        audioRef.current.volume = 0.1; // Ajusta el volumen aquí
        audioRef.current.play();
      }
    } else {
      setRainActive(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  };

  const handleFullscreen = () => {
    const elem = document.documentElement;
    if (!document.fullscreenElement) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen(); // Safari
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen(); // IE11
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen(); // Safari
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen(); // IE11
      }
    }
  };

  React.useEffect(() => {
    const unmuteAudio = () => {
      const audio = document.getElementById("rain-audio");
      if (audio && audio.muted) {
        audio.muted = false;
      }
      window.removeEventListener("mousemove", unmuteAudio);
    };
    window.addEventListener("mousemove", unmuteAudio);
    return () => window.removeEventListener("mousemove", unmuteAudio);
  }, []);

  React.useEffect(() => {
    function fullscreenListener() {
      setIsFullscreen(!!document.fullscreenElement);
    }
    document.addEventListener("fullscreenchange", fullscreenListener);
    return () =>
      document.removeEventListener("fullscreenchange", fullscreenListener);
  }, []);

  return (
    <>
      {/* Botón flotante para modo noche */}
      <button
        onClick={() => setNightMode((v) => !v)}
        className="floating-action-btn night"
        title={nightMode ? "Desactivar modo noche" : "Activar modo noche"}
        style={{ top: 128, right: 20 }}
      >
        {nightMode ? "🌙" : "☀️"}
      </button>
      {/* Botón flotante para pantalla completa */}
      <button
        onClick={handleFullscreen}
        className="floating-action-btn fullscreen"
        title={
          isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"
        }
      >
        {isFullscreen ? (
          // Collapse icon SVG (4 arrows inwards, invertido del expand)
          <img
            src={require("./resources/minimize.svg").default}
            alt="Minimizar"
            width={20}
            height={20}
          />
        ) : (
          // Expand icon SVG (4 arrows outwards)
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 3 21 3 21 9" />
            <line x1="14" y1="10" x2="21" y2="3" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="10" y1="14" x2="3" y2="21" />
            <polyline points="3 9 3 3 9 3" />
            <line x1="10" y1="10" x2="3" y2="3" />
            <polyline points="21 15 21 21 15 21" />
            <line x1="14" y1="14" x2="21" y2="21" />
          </svg>
        )}
      </button>
      {/* Botón flotante para activar/desactivar sonido y lluvia */}
      <button
        onClick={handleToggleRain}
        className={`floating-action-btn rain${rainActive ? " active" : ""}`}
        title={rainActive ? "Desactivar lluvia" : "Activar lluvia"}
      >
        <span
          role="img"
          aria-label={rainActive ? "Lluvia" : "Nube"}
          style={{ position: "relative" }}
        >
          {rainActive ? "🌧️" : "☁️"}
        </span>
      </button>
      <Canvas
        camera={{ position: [0, 30, 0] }}
        dpr={PERF.dpr}
        performance={{ min: 0.5, max: 1, debounce: 200 }}
        fog={{ color: "#b9d6c3", near: 30, far: 70 }}
      >
        {/* Estrellas solo en modo noche */}
        {nightMode && (
          <Stars
            radius={200}
            depth={60}
            count={PERF.starCount}
            factor={8}
            saturation={0}
            fade
            speed={0.5}
          />
        )}
        {/* <Sky azimuth={1} inclination={0.8} distance={1000} /> */}
        <ambientLight />
        <pointLight position={[10, 10, 10]} />

        <Suspense fallback={null}>
          <Grass
            instances={PERF.grassInstances}
            groundSegments={PERF.groundSegments}
            onAfterRender={() => setGrassLoaded(true)}
          />
        </Suspense>
        <Suspense fallback={null}>
          {/* Bichos voladores — InstancedMesh (3 draw calls total) */}
          <FlyingBugs trajectories={bugTrajectories} />
        </Suspense>
        <Suspense fallback={null}>
          {/* Hongos 3D sobre el terreno: mostrar todos si hay menos de 10, sino los últimos 10 */}
          {mushrooms.map((m, i) => (
            <Mushroom
              key={mushrooms.length <= 10 ? i : i + mushrooms.length - 10}
              type={i}
              position={m.position}
              scale={m.scale}
              rotation={m.rotation}
            />
          ))}
        </Suspense>
        <Suspense fallback={null}>
          {/* Lluvia solo si está activo */}
          {rainActive && <Rain count={PERF.rainCount} />}
        </Suspense>
        <OrbitControls
          minPolarAngle={Math.PI / 2.5}
          maxPolarAngle={Math.PI / 2.5}

          minDistance={20} // Limita más el zoom-in
          maxDistance={30} // Permite alejar más el zoom
        />
      </Canvas>
      <SpotifyPlayer url="https://open.spotify.com/playlist/7DrjNQh2imceLw0I6f9cbA" />
      <audio ref={audioRef} src="/rain.wav" loop volume={0.05} />
      {/* Mostrar ReflexionBox solo cuando Grass terminó de cargar */}
      {grassLoaded && <ReflexionBox />}
      {!grassLoaded && <LoadingScreen />}
    </>
  );
}
